import { useEffect, useRef, useState, useCallback } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, Trash2, Download, Loader2, Search, FileWarning, Plus, CheckCircle2 } from "lucide-react";
import { bytes, formatDate, relTime } from "@/lib/format";
import { toast } from "sonner";
import BulkActionBar from "@/components/BulkActionBar";
import ReasonDialog from "@/components/ReasonDialog";

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [clientF, setClientF] = useState("all");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingForm, setPendingForm] = useState({ name: "", client_id: "none", description: "" });
  const [pendingBusy, setPendingBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [docToDelete, setDocToDelete] = useState(null);
  const fileInputRef = useRef();
  const fulfilInputRef = useRef(null);
  const fulfilDocRef = useRef(null);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (clientF !== "all") params.client_id = clientF;
    if (pendingOnly) params.is_pending = true;
    api.get("/documents", { params }).then(({ data }) => {
      setDocs(data);
      setSelected(new Set());
    });
  }, [q, clientF, pendingOnly]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
  }, []);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        if (clientF !== "all") fd.append("client_id", clientF);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `Uploaded ${files.length} documents` : "Document uploaded");
      load();
    } catch (e) {
      toast.error(
        errorMessage(e, "Upload failed"),
      );
    } finally {
      setUploading(false);
    }
  };

  // Attaching a file to a pending placeholder fulfils it — the backend clears
  // is_pending and stores the file as v1 on the same record, so the row keeps
  // its name and history instead of becoming a second, unrelated document.
  const pickFulfilFile = (doc) => {
    fulfilDocRef.current = doc;
    fulfilInputRef.current?.click();
  };

  const fulfilPendingDoc = async (file) => {
    const doc = fulfilDocRef.current;
    if (!file || !doc) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/${doc.id}/version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${doc.name}" received`);
      load();
    } catch (e) {
      toast.error(
        errorMessage(e, "Upload failed"),
      );
    } finally {
      setUploading(false);
      fulfilDocRef.current = null;
    }
  };

  const [dragging, setDragging] = useState(false);

  const download = async (doc) => {
    const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Soft-delete is regulated: the backend requires an X-Change-Reason of at
  // least 5 chars, so the reason is collected via ReasonDialog.
  const del = async (reason) => {
    if (!docToDelete) return;
    try {
      await api.delete(`/documents/${docToDelete.id}`, { headers: { "X-Change-Reason": reason } });
      toast.success("Deleted");
      setDocToDelete(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Delete failed"));
    }
  };

  const createPending = async () => {
    if (!pendingForm.name.trim()) return;
    setPendingBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", pendingForm.name.trim());
      if (pendingForm.client_id !== "none") fd.append("client_id", pendingForm.client_id);
      if (pendingForm.description) fd.append("description", pendingForm.description);
      await api.post("/documents/pending", fd);
      toast.success("Pending document tracked");
      setPendingOpen(false);
      setPendingForm({ name: "", client_id: "none", description: "" });
      load();
    } catch (e) {
      toast.error(
        errorMessage(e, "Could not track document"),
      );
    } finally {
      setPendingBusy(false);
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => (s.size === docs.length ? new Set() : new Set(docs.map((d) => d.id))));
  };
  const bulkDelete = async () => {
    const reason = window.prompt(`Delete ${selected.size} document(s)? Reason (min 5 chars, required for audit):`);
    if (!reason || reason.trim().length < 5) return;
    try {
      const { data } = await api.post(
        "/bulk/documents/delete",
        { ids: Array.from(selected) },
        { headers: { "X-Change-Reason": reason.trim() } },
      );
      toast.success(`Deleted ${data.updated} document(s)`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk delete failed"));
    }
  };
  const bulkMarkReceived = async () => {
    try {
      const { data } = await api.post("/bulk/documents/mark-received", { ids: Array.from(selected) });
      toast.success(`Marked ${data.updated} as received`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk mark failed"));
    }
  };
  const anyPendingSelected = docs.some((d) => selected.has(d.id) && d.is_pending);

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-64">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search documents..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="docs-search" />
        </div>
        <Select value={clientF} onValueChange={setClientF}>
          <SelectTrigger className="h-9 w-52" data-testid="docs-filter-client"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={pendingOnly ? "default" : "outline"} size="sm" onClick={() => setPendingOnly((v) => !v)} data-testid="docs-filter-pending">
          <FileWarning size={12} className="mr-1.5" /> Pending only
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => setPendingOpen(true)} data-testid="track-pending-btn">
          <Plus size={13} className="mr-1.5" /> Track pending
        </Button>
        <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-btn">
          {uploading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Upload size={13} className="mr-1.5" />}
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            onFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="file-input"
        />
        <input
          ref={fulfilInputRef}
          type="file"
          hidden
          onChange={(e) => {
            fulfilPendingDoc(e.target.files?.[0]);
            e.target.value = "";
          }}
          data-testid="doc-fulfil-input"
        />
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files); }}
        className={`border ${dragging ? "border-foreground border-dashed bg-muted/30" : "border-border"} rounded-md overflow-hidden transition-colors`}
        data-testid="docs-dropzone"
      >
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 w-8">
                <Checkbox
                  checked={docs.length > 0 && selected.size === docs.length}
                  onCheckedChange={toggleAll}
                  data-testid="docs-select-all"
                  aria-label="Select all documents"
                />
              </th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Name</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Client</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Size</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Version</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Uploaded</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => {
              const isSelected = selected.has(d.id);
              return (
              <tr key={d.id} className={`border-b border-border hover:bg-muted/40 ${isSelected ? "bg-primary/5" : ""}`} data-testid={`doc-row-${d.id}`}>
                <td className="px-3 py-2.5">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleRow(d.id)}
                    data-testid={`doc-select-${d.id}`}
                    aria-label={`Select ${d.name}`}
                  />
                </td>
                <td className="px-3 py-2.5 font-medium">
                  <div className="flex items-center gap-2">
                    {d.is_pending ? <FileWarning size={13} className="text-amber-500" /> : <FileText size={13} className="text-muted-foreground" />}
                    <span className="truncate">{d.name}</span>
                    {d.is_pending && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-500/50 ml-2">Pending</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{d.client_id ? clientName(d.client_id) : "—"}</td>
                <td className="px-3 py-2.5 text-xs font-mono">{d.is_pending ? "—" : bytes(d.size)}</td>
                <td className="px-3 py-2.5 text-xs font-mono">{d.is_pending ? "—" : `v${d.current_version}`}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">{relTime(d.created_at)}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    {d.is_pending && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => pickFulfilFile(d)}
                        disabled={uploading}
                        title="Upload the received file"
                        data-testid={`doc-fulfil-${d.id}`}
                      >
                        <Upload size={13} />
                      </Button>
                    )}
                    {!d.is_pending && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(d)} title="Download" data-testid={`doc-download-${d.id}`}>
                        <Download size={13} />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => setDocToDelete(d)} title="Delete" data-testid={`doc-delete-${d.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </td>
              </tr>
              );
            })}
            {docs.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center">
                <div className="text-sm text-muted-foreground">
                  No documents yet. Drop files here or{" "}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="underline hover:text-foreground"
                    data-testid="docs-upload-empty"
                  >
                    upload one
                  </button>
                  .
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>
        <DialogContent className="max-w-md" data-testid="pending-dialog">
          <DialogHeader><DialogTitle>Track a pending document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Expected document name</Label>
              <Input value={pendingForm.name} onChange={(e) => setPendingForm({ ...pendingForm, name: e.target.value })} placeholder="e.g. Bank statement — March 2026" data-testid="pending-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Client</Label>
              <Select value={pendingForm.client_id} onValueChange={(v) => setPendingForm({ ...pendingForm, client_id: v })}>
                <SelectTrigger data-testid="pending-client"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note (optional)</Label>
              <Input value={pendingForm.description} onChange={(e) => setPendingForm({ ...pendingForm, description: e.target.value })} data-testid="pending-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingOpen(false)}>Cancel</Button>
            <Button onClick={createPending} disabled={!pendingForm.name.trim() || pendingBusy} data-testid="pending-submit">
              {pendingBusy && <Loader2 size={13} className="animate-spin mr-1.5" />}
              Track
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReasonDialog
        open={!!docToDelete}
        onOpenChange={(v) => !v && setDocToDelete(null)}
        onConfirm={del}
        title="Delete document"
        description={`"${docToDelete?.name ?? ""}" will be soft-deleted and retained in audit logs. This action requires a reason.`}
        confirmLabel="Delete document"
      />

      <BulkActionBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        actions={[
          ...(anyPendingSelected ? [{
            label: "Mark received",
            icon: <CheckCircle2 size={13} />,
            onClick: bulkMarkReceived,
            variant: "outline",
            testid: "bulk-mark-received",
          }] : []),
          {
            label: "Delete",
            icon: <Trash2 size={13} />,
            onClick: bulkDelete,
            variant: "destructive",
            testid: "bulk-delete-docs",
          },
        ]}
      />
    </div>
  );
}
